import * as React from 'react';

import {colors} from 'shells/colors/colors';

import {ChevronBorder} from '../lead/shared/components';

import styles from './chevron.module.css';

type Props = {
    hasStraightLeft?: boolean;
    hasStraightRight?: boolean;
    onClick?: (...args: any) => any;
    children?: any;
    // TODO: remove generic "style" prop, lock it down
    style?: any;
    hoverStyle?: any;
    hasShadow?: boolean;
    borderColor?: 'grey' | 'blue';
    isActive?: boolean;
    isPast?: boolean;
    isSelected?: boolean;
    variant?: Variant;
    height?: 'short' | 'tall';
    width?: number;
    hasRightMargin?: boolean;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
};

export type Variant = 'blue' | 'orange' | 'green' | 'grey';

type State = {
    isHovered: boolean;
};

export class Chevron extends React.PureComponent<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            isHovered: false,
        };
    }

    static defaultProps = {
        hasStraightLeft: false,
        hasStraightRight: false,
    };

    render() {
        let style = this.props.style || {};
        const {isSelected, isActive} = this.props;

        // This is used by the StageAddButton component
        if (this.props.borderColor === 'blue') {
            style.borderColor = colors.blue;
        }

        // Older uses of component pass in a style object for hover state
        if (this.state.isHovered && this.props.hoverStyle) {
            style = {...style, ...this.props.hoverStyle};
        }

        // Newer uses derive hover state from stage-status bit-flips
        if (this.state.isHovered && !isSelected && !this.props.hoverStyle) {
            style = {...style, textDecoration: 'underline', cursor: 'pointer'};
        }

        if (this.props.variant) {
            style = {...style, ...variantStyles(this.props)};
        }

        if (isSelected) {
            style = {...style, fontWeight: 500};
        }

        if (this.props.width) {
            style = {...style, width: `${this.props.width}px`};
        }

        if (this.props.hasRightMargin) {
            style = {...style, marginRight: '10px'};
        }

        if (this.props.height) {
            const height = this.props.height === 'short' ? `48px` : `64px`;
            style = {...style, height: height};
        }

        if (isActive) {
            style = {...style, height: '56px', marginTop: '-4px'};
        }

        return (
            <div
                style={style}
                onClick={this.props.onClick}
                onMouseEnter={this.handleMouseEnter}
                onMouseLeave={this.handleMouseLeave}
                className={styles.chevron}
            >
                <ChevronBorder
                    side='left'
                    type={this.props.hasStraightLeft ? 'straight' : 'bent'}
                    borderColor={style.borderColor || this.props.borderColor}
                    // @ts-expect-error
                    isHighlighted={isSelected}
                    bgColor={style.backgroundColor}
                />
                {this.props.children}
                <ChevronBorder
                    side='right'
                    type={this.props.hasStraightRight ? 'straight' : 'bent'}
                    borderColor={style.borderColor || this.props.borderColor}
                    // @ts-expect-error
                    isHighlighted={isSelected}
                    bgColor={style.backgroundColor}
                />
                {this.props.hasShadow ? <div className={styles.shadow} /> : undefined}
            </div>
        );
    }

    handleMouseEnter = () => {
        this.setState({isHovered: true});

        if (this.props.onMouseEnter) {
            this.props.onMouseEnter();
        }
    };

    handleMouseLeave = () => {
        this.setState({isHovered: false});

        if (this.props.onMouseLeave) {
            this.props.onMouseLeave();
        }
    };
}

function createBaseStyles(bgColor: string): any {
    return {backgroundColor: bgColor, borderColor: bgColor, color: colors.greyDk};
}

function variantStyles(props: Props): any {
    const {variant, isActive, isPast, isSelected} = props;
    let baseStyles: Record<string, any> = {};

    switch (variant) {
        case 'blue':
            baseStyles = createBaseStyles(colors.blueLt);
            if (isActive || isPast) {
                baseStyles.borderColor = colors.blue;
                baseStyles.backgroundColor = colors.blue;
                baseStyles.color = colors.offWhite;
            }

            if (isSelected) {
                baseStyles.borderColor = colors.blueDk;
            }
            break;
        case 'orange':
            baseStyles = createBaseStyles(colors.orangeLt);
            if (isActive || isPast) {
                baseStyles.borderColor = colors.backgroundHot;
                baseStyles.backgroundColor = colors.backgroundHot;
                baseStyles.color = colors.offWhite;
            }

            if (isSelected) {
                baseStyles.borderColor = colors.orange;
            }

            if (isSelected && isPast) {
                baseStyles.borderColor = colors.orangeDk;
            }
            break;
        case 'green':
            baseStyles = createBaseStyles(colors.greenLt);
            if (isActive || isPast) {
                baseStyles.borderColor = colors.lead;
                baseStyles.backgroundColor = colors.lead;
                baseStyles.color = colors.offWhite;
            }

            if (isSelected) {
                baseStyles.borderColor = colors.greenDk;
            }
            break;
        case 'grey':
            baseStyles = createBaseStyles(colors.offWhite);
            if (isActive || isPast) {
                baseStyles.borderColor = colors.offWhiteDk;
                baseStyles.backgroundColor = colors.offWhiteDk;
            }

            if (isSelected) {
                baseStyles.borderColor = colors.greyLt;
            }
            break;
    }

    return baseStyles;
}

export function loadingIconColor(variant: Variant, isPastOrActive: boolean) {
    // Reversing the isPastOrActive boolean so that we can use the inverse
    // background color as the color for the loading spinner.
    const baseStyles = variantStyles({variant, isActive: !isPastOrActive});

    return baseStyles.backgroundColor;
}
